import { useState } from "react";
import { Group, Loader, Stack, Text, Textarea } from "@mantine/core";
import { useOrderComment } from "@jasper/use-cases";
import { useDebouncedCallback } from "use-debounce";
import { CommentIndicator } from "../components/CommentIndicator";
import { CommentTextAreaWithFiles } from "../components/CommentTextAreaWithFiles";

// Text comment for the lab, saved as the user types (files are handled in the "Empreintes, photos & fichiers" section),
// followed by the comments of previous rounds, read only
export const OrderCommentField = ({ orderId }: { orderId: string }) => {
  const {
    activeOrderComment,
    previousOrderComments,
    isLoading,
    upsertActiveOrderComment,
  } = useOrderComment({ orderId });

  const [isSaving, setIsSaving] = useState(false);
  const [savingError, setSavingError] = useState<string>();

  const saveComment = useDebouncedCallback((comment: string) => {
    setIsSaving(true);
    setSavingError(undefined);
    upsertActiveOrderComment(comment)
      .catch(error => {
        console.error("Error while saving the order comment", error);
        setSavingError(error?.message ?? "Unknown error");
      })
      .finally(() => setIsSaving(false));
  }, 300);

  if (isLoading) {
    return <Loader size="sm" />;
  }

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text variant="eyebrow" c="dimmed">
            Commentaire pour le laboratoire · optionnel
          </Text>
          <CommentIndicator
            isUpdatingComment={isSaving}
            isUploadingFiles={false}
            updatingCommentError={savingError}
            activeOrderComment={activeOrderComment}
          />
        </Group>
        <Textarea
          aria-label="Commentaire pour le laboratoire"
          placeholder="Un commentaire sur la commande ? Livraison, délai, précision clinique…"
          defaultValue={activeOrderComment?.comment}
          onChange={event => saveComment(event.currentTarget.value)}
          autosize
          minRows={3}
          maxRows={8}
        />
      </Stack>

      {previousOrderComments.length > 0 && (
        <Stack gap="xs">
          <Text variant="eyebrow" c="dimmed">
            Commentaires précédents
          </Text>
          {previousOrderComments.map(comment => (
            <CommentTextAreaWithFiles
              key={comment.id}
              orderComment={comment}
              disabled
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
